'use client';
import { useEffect, useRef } from 'react';
import videojs from 'video.js';
import 'video.js/dist/video-js.css';

import 'dashjs';
import 'videojs-contrib-dash';

export type Props = {
    src?: string;
    autoPlay?: boolean;
    muted?: boolean;
    poster?: string;
};

export default function Stream({
    src,
    autoPlay = true,
    muted = true,
    poster = '/static/images/record.svg',
}: Props) {
    const ref = useRef<HTMLVideoElement | null>(null);
    const playerRef = useRef<any | null>(null);

    useEffect(() => {
        if (!ref.current) return;

        const player = videojs(ref.current, {
            autoplay: autoPlay,
            muted,
            controls: true,
            preload: 'auto',
            fluid: true,
            poster,
            errorDisplay: false,
            controlBar: {
                volumePanel: { inline: false }, // show vertical volume on small widths
                remainingTimeDisplay: true,
                currentTimeDisplay: true,
                timeDivider: true,
                progressControl: true,
                liveDisplay: true,
                fullscreenToggle: true,
                pictureInPictureToggle: true,
            },
            sources: src ? [{ src, type: 'application/dash+xml' }] : [],
        });

        playerRef.current = player;

        player.ready(() => {
            const posterComp = () => player.getChild('posterImage') as any;

            const showPoster = () => {
                try {
                    player.pause();
                    player.poster(poster);
                    posterComp()?.show?.();
                } catch { }
            };

            const hidePoster = () => {
                try {
                    posterComp()?.hide?.();
                } catch { }
            };

            // Start with the poster visible until we actually play
            showPoster();

            const onError = () => {
                showPoster();
                if (!src) return;
                window.setTimeout(() => {
                    player.reset();
                    player.poster(poster);
                    player.src([{ src, type: 'application/dash+xml' }]);
                }, 1200);
            };

            const onCanPlay = async () => {
                if (!autoPlay) return;
                try {
                    await player.play();
                } catch {
                    // Autoplay blocked by browser — keep poster until user clicks
                    showPoster();
                }
            };

            const onPlaying = () => hidePoster();
            const onEnded = () => showPoster();

            player.on('error', onError);
            player.on('canplay', onCanPlay);
            player.on('playing', onPlaying);
            player.on('ended', onEnded);

            // Cleanup listeners on dispose
            player.one('dispose', () => {
                player.off('error', onError);
                player.off('canplay', onCanPlay);
                player.off('playing', onPlaying);
                player.off('ended', onEnded);
            });
        });

        return () => {
            player.dispose();
            playerRef.current = null;
        };
    }, [src, autoPlay, muted, poster]);

    return (
        <div className="w-full max-w-5xl mx-auto p-4">
            <video
                ref={ref}
                className="video-js vjs-default-skin vjs-big-play-centered rounded-xl shadow-lg"
                poster={poster}
                playsInline
            />
        </div>
    );
}
